/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { ISchemaConstAnyModel, isSchemaConstAnyModel } from "@adapptio/lib-blueprint";
import {
	INSPECTOR_NODE_TYPE,
	isInspectorNodeDefault,
	isInspectorNodeList,
	isInspectorNodeMap,
	TInspectorNode
} from "../InspectorNodeTypes";
import { IInspectorControl_Any, INSPECTOR_CONTROL_TYPE } from "../controls";
import { mapModelToNodes } from "../mapModelToNodes";
import { mapperDescriptor } from "../../modules/Core/ReferenceGuide/mappers/mapperDescriptor";
import { mapperExamples } from "../../modules/Core/ReferenceGuide/mappers/mapperExamples";
import { getParentComponentName } from "../utils";

export function constAnyMapper(model: ISchemaConstAnyModel): TInspectorNode {
	if (!isSchemaConstAnyModel(model)) {
		return;
	}

	const controls = [];
	const children = [];

	const anyControl: IInspectorControl_Any = {
		key: String(model.nodeId),
		type: INSPECTOR_CONTROL_TYPE.ANY,
		model
	};

	const valueNode = mapModelToNodes(model.value);

	if (isInspectorNodeList(valueNode) || isInspectorNodeMap(valueNode)) {
		return {
			type: INSPECTOR_NODE_TYPE.DEFAULT,
			key: `${model.nodeId}`,
			label: model.schema.opts.label,
			tooltip: model.schema.opts.description,
			options: model.schema.opts["options"],
			required: model.schema.opts.constraints?.required || false,
			referenceData: getParentComponentName(model),
			descriptor: mapperDescriptor(model.schema),
			deps: [ ...(valueNode?.deps ?? []), model.value, model ],
			children: [
				() => {
					const updatedValueNode = mapModelToNodes(model.value);

					return {
						...updatedValueNode,
						controls: [ ...updatedValueNode.controls, anyControl ]
					};
				}
			],
			controls: []
		};
	}

	if (isInspectorNodeDefault(valueNode)) {
		controls.push(...valueNode.controls);
		children.push(...valueNode.children);
	}

	controls.push(anyControl);

	return {
		type: INSPECTOR_NODE_TYPE.DEFAULT,
		key: `${model.nodeId}`,
		label: model.schema.opts.label,
		tooltip: model.schema.opts.description,
		default: model.schema.opts["default"] || null,
		required: model.schema.opts.constraints?.required || false,
		options: model.schema.opts["options"],
		referenceData: getParentComponentName(model),
		descriptor: mapperDescriptor(model.schema),
		examples: mapperExamples(model.schema),
		deps: [ model, model.value, ...(valueNode?.deps || []) ],
		controls: controls,
		children: children
	};
}
